import { MediaQuery } from '../../Foundation';

export class MainNavigation2 {
  /**
   * Creates a new instance of MainNavigation2.
   * @param {HTMLElement} element Main navigation portal element.
   */
  constructor(element) {
    // Must match the 'lg' breakpoint the navigation stylesheets switch on,
    // otherwise the 992px-1199px range renders the desktop layout while the
    // script still treats the menu as mobile.
    this._mediaQueryBreakPoint = 'lg';
    this._portal = null;
    this._body = null;
    this._rootTriggers = [];
    this._openTriggers = [];
    this._closeButton = null;
    this._layers = [];
    this._subTriggers = [];
    this._layerCloseButtons = [];
    this._rootLayer = null;
    this._props = null;
    this._animationCleanupTimeout = null;
    this._layerAnimationCleanupTimeout = null;
    this._animationDurationMs = 350;
    this._scrollLockTop = 0;

    this._portal = element;
    this._body = document.body;
    this._rootLayer = this._portal.querySelector('[data-main-navigation-layer-id="root"]');

    const desktopRootTriggers = Array.from(
      document.querySelectorAll(
        '[data-main-navigation-root-desktop-host] [data-main-navigation-layer-trigger]',
      ),
    );

    const mobileRootTriggers = Array.from(
      this._portal.querySelectorAll(
        '[data-main-navigation-root-mobile-host] [data-main-navigation-layer-trigger]',
      ),
    );

    this._rootTriggers = Array.from(new Set([...desktopRootTriggers, ...mobileRootTriggers]));
    this._openTriggers = Array.from(document.querySelectorAll('[data-main-navigation-open]'));
    this._closeButton = this._portal.querySelector('[data-main-navigation-close]');
    this._layers = Array.from(this._portal.querySelectorAll('[data-main-navigation-layer-id]'));
    this._subTriggers = Array.from(this._portal.querySelectorAll('[data-layer-sub-trigger]'));

    this._layerCloseButtons = Array.from(
      this._portal.querySelectorAll('[data-main-navigation-layer-close]'),
    );

    this._props = {
      isOpen: false,
      activeLayerId: null,
      lastFocusTrigger: null,
      lastInputWasKeyboard: false,
      isMobile: MediaQuery.below(this._mediaQueryBreakPoint),
    };

    element.MainNavigation2 = this;

    this._syncInitialLayerVisibility();
    this._closeAll({ returnFocus: false, keepSubState: true, animate: false });

    this._bindEvents();
  }

  _bindEvents() {
    this._rootTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this._onRootTriggerClick.bind(this));
      trigger.addEventListener('keydown', this._onRootTriggerKeyDown.bind(this));
    });

    this._subTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this._onSubTriggerClick.bind(this));
    });

    this._openTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this._onOpenTriggerClick.bind(this));
    });

    this._layerCloseButtons.forEach((button) => {
      button.addEventListener('click', this._onLayerCloseClick.bind(this));
    });

    if (this._closeButton) {
      this._closeButton.addEventListener('click', this._onCloseButtonClick.bind(this));
    }

    document.addEventListener('click', this._onDocumentClick.bind(this));
    document.addEventListener('keydown', this._onDocumentKeyDown.bind(this));
    window.addEventListener('change.mq', this._onMediaQueryChange.bind(this));
  }

  _onRootTriggerClick(event) {
    event.preventDefault();

    const trigger = event.currentTarget;
    const targetLayerId = trigger.getAttribute('data-main-navigation-layer-trigger');

    if (!targetLayerId) {
      return;
    }

    this._props.lastFocusTrigger = trigger;

    if (this._props.isOpen && this._props.activeLayerId === targetLayerId) {
      this._closeAll();
      return;
    }

    this._openLayerById(targetLayerId, trigger);
  }

  _onRootTriggerKeyDown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this._props.lastInputWasKeyboard = true;
    this._onRootTriggerClick(event);
  }

  _onSubTriggerClick(event) {
    const trigger = event.currentTarget;
    const subLayerId = trigger.getAttribute('data-layer-sub-trigger');
    const parentLayer = trigger.closest('[data-main-navigation-layer-id]');

    if (!subLayerId || !parentLayer) {
      return;
    }

    event.preventDefault();

    const currentSubLayers = Array.from(
      parentLayer.querySelectorAll('[data-main-navigation-sub-layer-id]'),
    );

    const currentSubTriggers = Array.from(parentLayer.querySelectorAll('[data-layer-sub-trigger]'));

    const targetSubLayer = parentLayer.querySelector(
      '[data-main-navigation-sub-layer-id="' + subLayerId + '"]',
    );

    currentSubTriggers.forEach((button) => button.classList.remove('is-active'));
    trigger.classList.add('is-active');

    currentSubLayers.forEach((item) => {
      const isTarget = item === targetSubLayer;
      item.classList.toggle('is-hidden', !isTarget);
      item.classList.toggle('is-active', isTarget);
      item.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
      item.setAttribute('data-main-navigation-layer-state', isTarget ? 'active' : 'inactive');
    });
  }

  _onOpenTriggerClick(event) {
    event.preventDefault();

    this._props.lastFocusTrigger = event.currentTarget;
    this._props.lastInputWasKeyboard = event.detail === 0;

    if (this._props.isOpen) {
      return;
    }

    this._showMainLevelOnly();
  }

  _onCloseButtonClick() {
    this._closeAll();
  }

  _onLayerCloseClick(event) {
    this._props.lastInputWasKeyboard = event.detail === 0;
    this._resetSubLayers();

    if (this._props.isMobile) {
      this._showMainLevelOnly();
      return;
    }

    this._closeAll();
  }

  _onDocumentClick(event) {
    if (!this._props.isOpen || this._props.isMobile) {
      return;
    }

    const target = event.target;
    const clickedInsidePortal = this._portal.contains(target);
    const clickedOnTrigger = this._rootTriggers.some((trigger) => trigger.contains(target));

    if (!clickedInsidePortal && !clickedOnTrigger) {
      this._closeAll();
    }
  }

  _onDocumentKeyDown(event) {
    if (!this._props.isOpen) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this._closeAll();
      return;
    }

    if (event.key === 'Tab') {
      this._trapFocus(event);
    }
  }

  _onMediaQueryChange() {
    const wasMobile = this._props.isMobile;
    this._props.isMobile = MediaQuery.below(this._mediaQueryBreakPoint);

    if (wasMobile && !this._props.isMobile) {
      this._body.classList.remove('is-visible');
    }

    if (this._props.isOpen) {
      this._closeAll({ returnFocus: false });
    }
  }

  _openLayerById(layerId, trigger) {
    if (!layerId) {
      return;
    }

    const targetLayer = this._layers.find(
      (layer) => layer.getAttribute('data-main-navigation-layer-id') === layerId,
    );

    if (!targetLayer) {
      return;
    }

    this._resetSubLayers({ restoreFirst: true });

    const keepRootVisible = this._props.isMobile && this._props.isOpen;

    this._layers.forEach((layer) => {
      const isTarget = layer === targetLayer;
      const isRoot = layer === this._rootLayer;

      if (keepRootVisible && isRoot) {
        layer.setAttribute('aria-hidden', 'true');
        return;
      }

      layer.classList.toggle('is-hidden', !isTarget);
      layer.classList.toggle('is-active', isTarget);
      layer.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
    });

    this._rootTriggers.forEach((button) => {
      const isActive = button === trigger;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    this._props.activeLayerId = layerId;

    if (!this._props.isOpen) {
      this._setPortalVisibility(true);
    } else if (keepRootVisible) {
      this._setLayerAnimationState(targetLayer, true);
    }

    if (this._props.lastInputWasKeyboard) {
      const focusTarget = this._getFocusableElements(targetLayer)[0];

      if (focusTarget) {
        this._focusWithoutScroll(focusTarget);
      }
    }

    this._props.lastInputWasKeyboard = false;
  }

  _setPortalVisibility(isVisible, options = {}) {
    const settings = {
      animate: true,
      ...options,
    };

    this._props.isOpen = isVisible;
    this._portal.classList.toggle('is-hidden', !isVisible);
    this._portal.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

    this._openTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isVisible ? 'true' : 'false');
    });

    if (isVisible && this._props.isMobile) {
      this._lockPageScroll();
    }

    if (!isVisible) {
      this._unlockPageScroll();
    }

    if (this._props.isMobile) {
      this._body.classList.toggle('is-visible', isVisible);
    }

    if (isVisible) {
      this._portal.scrollTop = 0;

      window.requestAnimationFrame(() => {
        this._portal.scrollTop = 0;
      });
    }

    if (settings.animate) {
      this._setPortalAnimationState(isVisible);
    }
  }

  _closeAll(options = {}) {
    const settings = {
      returnFocus: true,
      keepSubState: false,
      animate: true,
      ...options,
    };

    this._setPortalVisibility(false, { animate: settings.animate });

    this._props.activeLayerId = null;

    this._rootTriggers.forEach((trigger) => {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    });

    if (this._layerAnimationCleanupTimeout) {
      window.clearTimeout(this._layerAnimationCleanupTimeout);
      this._layerAnimationCleanupTimeout = null;
    }

    this._layers.forEach((layer) => {
      layer.classList.add('is-hidden');
      layer.classList.remove(
        'is-active',
        'is-layer-animating',
        'is-layer-animating-in',
        'is-layer-animating-out',
      );
      layer.setAttribute('aria-hidden', 'true');
    });

    if (!settings.keepSubState) {
      // On desktop every layer is hidden outright here, so the first category is
      // restored ready for the next open. Mobile keeps the plain reset because its
      // close animation depends on all sub-layers staying hidden.
      this._resetSubLayers({ restoreFirst: !this._props.isMobile });
    }

    if (settings.returnFocus && this._props.lastFocusTrigger) {
      this._props.lastFocusTrigger.focus();
    }
  }

  _showMainLevelOnly() {
    this._props.activeLayerId = this._rootLayer ? 'root' : null;

    this._rootTriggers.forEach((trigger) => {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    });

    if (this._props.isMobile && this._props.isOpen) {
      // Mobile: root is already visible underneath; animate sublayer out.
      const activeSubLayer = this._layers.find(
        (layer) => layer !== this._rootLayer && !layer.classList.contains('is-hidden'),
      );

      if (activeSubLayer) {
        this._setLayerAnimationState(activeSubLayer, false);
      }

      if (this._rootLayer) {
        this._rootLayer.setAttribute('aria-hidden', 'false');
        this._rootLayer.classList.add('is-active');
      }

      this._resetSubLayers();
    } else {
      this._layers.forEach((layer) => {
        const isRootLayer = this._rootLayer && layer === this._rootLayer;
        layer.classList.toggle('is-hidden', !isRootLayer);
        layer.classList.toggle('is-active', Boolean(isRootLayer));
        layer.setAttribute('aria-hidden', isRootLayer ? 'false' : 'true');
      });

      this._resetSubLayers({ restoreFirst: true });
      this._setPortalVisibility(true);
    }

    if (this._props.lastInputWasKeyboard) {
      const focusTarget = this._rootLayer
        ? this._rootLayer.querySelector('[data-main-navigation-layer-trigger], a, button')
        : null;

      if (focusTarget) {
        this._focusWithoutScroll(focusTarget);
      }
    }

    this._props.lastInputWasKeyboard = false;
  }

  _focusWithoutScroll(element, options = {}) {
    if (!element) {
      return;
    }

    const focusOptions = { preventScroll: true };

    if ('focusVisible' in options) {
      focusOptions.focusVisible = options.focusVisible;
    }

    try {
      element.focus(focusOptions);
    } catch (error) {
      element.focus();
    }
  }

  _lockPageScroll() {
    if (this._body.classList.contains('has-main-navigation-scroll-lock')) {
      return;
    }

    this._scrollLockTop = window.pageYOffset || window.scrollY || 0;

    this._body.classList.add('has-main-navigation-scroll-lock');
    this._body.style.position = 'fixed';
    this._body.style.top = '-' + this._scrollLockTop + 'px';
    this._body.style.left = '0';
    this._body.style.right = '0';
    this._body.style.width = '100%';
    this._body.style.overflow = 'hidden';
  }

  _unlockPageScroll() {
    if (!this._body.classList.contains('has-main-navigation-scroll-lock')) {
      return;
    }

    this._body.classList.remove('has-main-navigation-scroll-lock');
    this._body.style.position = '';
    this._body.style.top = '';
    this._body.style.left = '';
    this._body.style.right = '';
    this._body.style.width = '';
    this._body.style.overflow = '';

    window.scrollTo(0, this._scrollLockTop);
  }

  _syncInitialLayerVisibility() {
    this._layers.forEach((layer) => {
      const isHidden =
        layer.getAttribute('aria-hidden') === 'true' || layer.classList.contains('is-hidden');

      layer.classList.toggle('is-hidden', isHidden);
    });

    const layerItems = Array.from(
      this._portal.querySelectorAll('[data-main-navigation-layer-level]'),
    );

    layerItems.forEach((item) => {
      const isHidden = item.getAttribute('aria-hidden') === 'true';
      item.classList.toggle('is-hidden', isHidden);
    });
  }

  // When `restoreFirst` is set, the first category is re-activated so a layer
  // reopens showing content instead of an empty panel (matching the state the
  // markup is rendered in). It is opt-in because the mobile close animation
  // relies on every sub-layer being hidden while it animates out.
  _resetSubLayers(options = {}) {
    const { restoreFirst = false } = options;

    this._layers.forEach((layer) => {
      const subLayers = Array.from(layer.querySelectorAll('[data-main-navigation-sub-layer-id]'));
      const rootLevelItem = layer.querySelector('[data-main-navigation-layer-level="0"]');
      const subTriggers = Array.from(layer.querySelectorAll('[data-layer-sub-trigger]'));

      subLayers.forEach((item, index) => {
        const isActive = restoreFirst && index === 0;
        item.classList.toggle('is-hidden', !isActive);
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        item.setAttribute('data-main-navigation-layer-state', isActive ? 'active' : 'inactive');
      });

      if (rootLevelItem) {
        rootLevelItem.classList.remove('is-hidden');
        rootLevelItem.setAttribute('aria-hidden', 'false');
      }

      subTriggers.forEach((trigger, index) =>
        trigger.classList.toggle('is-active', restoreFirst && index === 0),
      );
    });
  }

  _trapFocus(event) {
    const focusableElements = this._getFocusableElements(this._portal);

    if (!focusableElements.length) {
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (focusableElements.indexOf(activeElement) === -1) {
      event.preventDefault();
      (event.shiftKey ? lastElement : firstElement).focus();
      return;
    }

    if (event.shiftKey && activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  _getFocusableElements(container) {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(container.querySelectorAll(selector)).filter((element) => {
      if (element.offsetParent === null) {
        return false;
      }

      if (this._hasHiddenAriaAncestor(element, container)) {
        return false;
      }

      if (element.getAttribute('aria-hidden') === 'true') {
        return false;
      }

      return true;
    });
  }

  _hasHiddenAriaAncestor(element, stopAt) {
    let currentElement = element;

    while (currentElement && currentElement !== stopAt) {
      if (currentElement.getAttribute('aria-hidden') === 'true') {
        return true;
      }

      currentElement = currentElement.parentElement;
    }

    return false;
  }

  _setLayerAnimationState(layer, isOpening) {
    if (this._layerAnimationCleanupTimeout) {
      window.clearTimeout(this._layerAnimationCleanupTimeout);
      this._layerAnimationCleanupTimeout = null;
    }

    layer.classList.add('is-layer-animating');
    layer.classList.toggle('is-layer-animating-in', isOpening);
    layer.classList.toggle('is-layer-animating-out', !isOpening);

    this._layerAnimationCleanupTimeout = window.setTimeout(() => {
      layer.classList.remove(
        'is-layer-animating',
        'is-layer-animating-in',
        'is-layer-animating-out',
      );

      if (!isOpening) {
        layer.classList.add('is-hidden');
        layer.classList.remove('is-active');
        layer.setAttribute('aria-hidden', 'true');
      }

      this._layerAnimationCleanupTimeout = null;
    }, this._animationDurationMs);
  }

  _setPortalAnimationState(isOpening) {
    if (this._animationCleanupTimeout) {
      window.clearTimeout(this._animationCleanupTimeout);
      this._animationCleanupTimeout = null;
    }

    this._portal.classList.add('is-animating');
    this._portal.classList.toggle('is-animating-in', isOpening);
    this._portal.classList.toggle('is-animating-out', !isOpening);

    this._animationCleanupTimeout = window.setTimeout(() => {
      this._portal.classList.remove('is-animating');
      this._portal.classList.remove('is-animating-in');
      this._portal.classList.remove('is-animating-out');
      this._animationCleanupTimeout = null;
    }, this._animationDurationMs);
  }
}
